<template>
  <swiper-container
    v-if="data.items"
    :id="data.id"
    ref="swiperEl"
    init="false"
    class="group h-[568px] lg:h-[800px] 2xl:h-[840px] 4xl:h-[1144px]"
    :style="{ 'margin-top': `-${isDesktop ? 93 : 0}px` }"
  >
    <div
      slot="container-end"
      class="container absolute bottom-6 left-0 right-0 z-10 md:px-[6%] xl:px-[11%] 4xl:max-w-[2018px] 4xl:px-[40px]"
    >
      <div
        ref="pagination"
        class="swiper-banners-pagination flex justify-center"
      ></div>
    </div>

    <div
      v-show="isDesktop"
      slot="container-start"
      class="nav-prev absolute bottom-0 left-0 top-0 z-50 flex w-16 cursor-pointer items-center justify-center rounded-l-sm text-white opacity-0 transition duration-300 group-hover:opacity-100"
    >
      <UiIcon icon="sliderArrowLeft"></UiIcon>
    </div>

    <div
      v-show="isDesktop"
      slot="container-start"
      class="nav-next absolute bottom-0 right-0 top-0 z-50 flex w-16 cursor-pointer items-center justify-center rounded-l-sm text-white opacity-0 transition duration-300 group-hover:opacity-100"
    >
      <UiIcon icon="sliderArrowRight"></UiIcon>
    </div>

    <swiper-slide v-for="(banner, i) in data.items" :key="i">
      <div
        class="flex h-full items-center bg-cover bg-top"
        :style="getBackgroundImage(banner)"
      >
        <div
          class="container relative flex h-full items-center justify-between md:grid md:h-auto md:grid-cols-2 md:flex-row md:gap-x-[7%] md:px-[6%] md:pt-0 xl:grid-cols-[39%_54%] xl:px-[11%] 3xl:grid-cols-[37%_56%] 4xl:px-[40px]"
          :class="{ 'pt-[230px] md:pt-0': !banner.markers }"
        >
          <div
            v-if="banner.markers && banner.markers.length"
            class="absolute left-0 right-0 top-[75px] mx-auto w-[280px] w-full -translate-x-[10%] md:relative md:top-0 md:order-last md:mr-0 md:w-full md:translate-x-0 xl:-mr-[6%]"
          >
            <div class="relative pt-[73.333%] md:mt-0">
              <div class="markers absolute inset-0 opacity-0">
                <div
                  v-if="banner.markersCanvasImage"
                  class="absolute inset-0 scale-[1.86] transform-gpu bg-contain bg-center bg-no-repeat md:-translate-x-[12%] 3xl:-translate-y-[2%]"
                  :data-background="getCanvasImage(banner.markersCanvasImage)"
                ></div>

                <UiMarkerPoint
                  v-for="(marker, mi) in banner.markers"
                  :key="mi"
                  :marker="marker"
                  class="opacity-0"
                />
              </div>
            </div>
          </div>

          <div
            class="absolute bottom-[60px] left-6 right-6 z-10 flex max-w-full flex-col self-start text-white sm:self-center md:relative md:bottom-0 md:left-0 md:right-0"
          >
            <UiSectionTitle
              :tag="i === 0 ? 'h1' : ''"
              :title="banner.title"
              class="title title mb-2 text-3xl uppercase leading-none opacity-0 md:text-5xl lg:text-[3.5rem]"
            />

            <div
              class="text mb-4 text-md font-normal leading-[1.8rem] opacity-0"
              v-html="banner.text"
            />

            <UiButton
              v-if="banner.button && banner.button.isVisible"
              :to="banner.button.link"
              size="md"
              target="_blank"
              class="button opacity-0"
            >
              <span v-html="banner.button.text" />
            </UiButton>
          </div>
        </div>
      </div>
    </swiper-slide>
  </swiper-container>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useElementHover, useMousePressed } from '@vueuse/core';
import { hideAllPoppers } from 'floating-vue';
import {
  useSwiperAutoplayProgress,
  useBreakpoint,
  useMediaAsset,
  useSwiperElement,
} from '~/composables';
import { isTouchDevice } from '~/helpers';

interface Props {
  data?: any;
}

withDefaults(defineProps<Props>(), {
  data: () => ({}),
});

const { getAssetUrl } = useMediaAsset();

const duration = 0.65;

const animations = {
  slideUp: [
    { opacity: 0, yPercent: 50, ease: 'circ' },
    { opacity: 1, yPercent: 0, ease: 'circ', duration },
  ],
  fadeIn: [
    { opacity: 0, ease: 'linear' },
    { opacity: 1, ease: 'linear', duration: 1.5 },
  ],
  fadeInWithBlur: [
    { opacity: 0, backdropFilter: 'blur(0px)', ease: 'linear' },
    { opacity: 1, backdropFilter: 'blur(10px)', ease: 'linear', duration: 1.5 },
  ],
};

const runSlideStartAnimation = ($gsap: GSAP, rootEl: HTMLElement) => {
  const { fadeIn, fadeInWithBlur, slideUp } = animations;
  const activeSlide = rootEl.querySelector('.swiper-slide-active');

  if (!activeSlide) return;

  const markersCanvas = activeSlide!.querySelector('.markers');
  let delay = 0;

  if (markersCanvas) {
    delay += 0.8;
    $gsap.fromTo(markersCanvas, fadeIn[0], fadeIn[1]);

    markersCanvas!
      .querySelectorAll('.marker')
      .forEach((marker: Element) =>
        $gsap.fromTo(marker, fadeInWithBlur[0], fadeInWithBlur[1]),
      );
  }

  activeSlide!
    .querySelectorAll('.title, .text, .button')
    .forEach((selector, i) => {
      delay += 0.4 * (i || 1);
      $gsap.fromTo(selector, slideUp[0], slideUp[1]).delay(delay);
    });
};

const runSlideEndAnimation = ($gsap: GSAP, rootEl: HTMLElement) => {
  const { fadeIn, slideUp } = { ...animations };
  fadeIn[0].duration = 0.1;
  slideUp[0].duration = 0.1;

  rootEl
    .querySelectorAll(
      '.swiper-slide-next .markers, .swiper-slide-prev .markers',
    )
    .forEach((marker: Element) => {
      $gsap.fromTo(marker, fadeIn[1], fadeIn[0]);
    });

  rootEl
    .querySelectorAll(
      '.swiper-slide-next .title, .swiper-slide-prev .title, .swiper-slide-prev .text,  .swiper-slide-next .text,  .swiper-slide-prev .button,  .swiper-slide-next .button',
    )
    .forEach((selector) => $gsap.fromTo(selector, slideUp[1], slideUp[0]));
};

const { $gsap } = useNuxtApp();
const { isBiggerThanBreakpoint: isDesktop } = useBreakpoint('lg');

const pagination = ref();

const runAnimation = (type: string) => {
  if (!swiperEl.value) return;

  (type === 'start' ? runSlideStartAnimation : runSlideEndAnimation)(
    $gsap,
    swiperEl.value,
  );
};

const swiperOptions = {
  speed: 1_000,
  pagination: {
    clickable: true,
    el: '.swiper-banners-pagination',
  },
  navigation: {
    prevEl: '.nav-prev',
    nextEl: '.nav-next',
    disabledClass: 'group-hover:opacity-50 group-hover:hover:opacity-75',
  },
  autoplay: {
    delay: 8_000,
    disableOnInteraction: false,
    waitForTransition: false,
  },
  loop: true,
  slidesPerView: 1,
  observer: true,
  on: {
    activeIndexChange: () => {
      pagination.value
        ?.querySelectorAll('.swiper-pagination-bullet')
        .forEach((el: HTMLElement) => {
          el.style.background = 'rgba(255, 255, 255, 0.4)';
        });
    },
    slideChangeTransitionStart: () => {
      hideAllPoppers();
    },
    slideChangeTransitionEnd: () => {
      runAnimation('end');
      runAnimation('start');
    },
  },
};

const swiperEl = useSwiperElement(swiperOptions);

const { progress } = useSwiperAutoplayProgress(swiperEl as any);
const isHovered = useElementHover(swiperEl as any);
const { pressed } = useMousePressed({ target: swiperEl as any });

const getCanvasImage = (src: string) =>
  getAssetUrl(src, isDesktop.value ? {} : { height: 600 });

const getBackgroundImage = (banner: any) => {
  return {
    backgroundImage: `url('${
      isDesktop.value
        ? getAssetUrl(banner.image)
        : getAssetUrl(banner.imageMobile)
    }')`,
  };
};

const swiperAutoplayToggle = (state: boolean) => {
  if (state) swiperEl.value?.swiper?.autoplay.stop();
  else swiperEl.value?.swiper?.autoplay.start();
};

watch(isHovered, (newValue) => {
  if (isTouchDevice()) return;
  swiperAutoplayToggle(newValue as any);
});

watch(pressed, (newValue) => {
  if (!isTouchDevice()) return;
  swiperAutoplayToggle(newValue as any);
});

watch(progress, (newValue) => {
  const activeBullet = pagination.value?.querySelector(
    '.swiper-pagination-bullet-active',
  );

  if (activeBullet)
    activeBullet.style.background = `linear-gradient(90deg, #00A7E1 ${newValue}%, rgba(255, 255, 255, 0.4) 0%)`;
});

onMounted(() => {
  runAnimation('start');
});
</script>

<style lang="sass">
.v-popper__popper
  width: auto
  max-width: calc(100% - 48px)
  outline: 0 !important

  &.v-popper__popper--no-positioning
    position: fixed
    top: auto
    left: 24px
    right: 24px
    bottom: 40px
    margin: auto

  .v-popper__inner
    overflow: hidden
    border: 0

.swiper-banners-pagination
  column-gap: 1rem
  height: 4px

  .swiper-pagination-bullet
    flex-grow: 1
    height: 4px
    border-radius: 4px
    background: rgba(255, 255, 255, 0.4)
    cursor: pointer
    transition: all 0.3s
</style>

<style lang="sass" scoped>
.marker
  background: linear-gradient(180deg, rgba(126, 126, 126, 0.24) 0%, rgba(187, 187, 187, 0.16) 100%)
  backdrop-filter: blur(0px)

  &::before
    transform: translate(-50%, -50%)
    filter: drop-shadow(0px 4px 24px rgba(0, 0, 0, 0.16))

.cursor-arrow-left
  cursor: url("/images/cursor-arrow-left.png"), auto

.cursor-arrow-right
  cursor: url("/images/cursor-arrow-right.png"), auto
</style>
